// Les locales date-fns sont importées par **sous-chemin** et non depuis le barrel
// `date-fns/locale` : Metro ne fait pas de tree-shaking, et le barrel embarque les
// ~180 locales du paquet. Mesuré sur le bundle iOS de driver-app : 531 modules,
// 896 Ko, pour deux locales réellement utilisées.
import { enUS as dfnEn } from 'date-fns/locale/en-US';
import { fr as dfnFr } from 'date-fns/locale/fr';
import { I18n } from 'i18n-js';
import type { TranslateOptions } from 'i18n-js';

import en from './locales/en.json';
import fr from './locales/fr.json';
import type { Leaves, LocalizedDictionary } from './types';
import { getConfig } from '../../config';
import { storage } from '../storage/storage';

const supportedLocales = ['fr', 'en'] as const;
export type SupportedLocales = (typeof supportedLocales)[number];

const dateFnsLocales = { fr: dfnFr, en: dfnEn };

let _i18n: I18n | null = null;

/**
 * Locale posée explicitement par l'app, prioritaire sur la résolution par config.
 *
 * Stockée hors de l'instance parce qu'une app peut l'imposer au chargement de son
 * module d'i18n, ce qui arrive **avant** `initSharedLib()` selon l'ordre des
 * imports. Sans ça, `getConfig()` serait lu trop tôt et l'ordre des imports —
 * qu'un tri automatique peut changer sans prévenir — deviendrait un crash au
 * démarrage.
 */
let _activeLocale: SupportedLocales | null = null;

const resolveInitialLocale = () =>
  supportedLocales.find(l =>
    (getConfig().forceLocale || storage.getItem('locale') || getConfig().systemLocale || '').startsWith(l),
  ) || 'en';

const getI18n = () => {
  if (!_i18n) {
    _i18n = new I18n({ en, fr });

    _i18n.defaultLocale = 'en';
    _i18n.enableFallback = true;

    // `getConfig()` n'est lu que si l'app n'a pas déjà imposé sa langue.
    _i18n.locale = _activeLocale ?? resolveInitialLocale();
  }
  return _i18n;
};

export const getDateLocale = () => dateFnsLocales[getLocale()];

export const t = (key: Leaves<LocalizedDictionary>, options?: TranslateOptions) => getI18n().t(key, options);
export const tPlural = (key: string, options?: TranslateOptions) => getI18n().t(key, options);
export const getLocale = () => getI18n().locale as SupportedLocales;

/**
 * Change la langue **sans** recharger l'app, pour les apps qui remontent leur
 * arbre elles-mêmes (`driver-app` remonte son navigateur sur la locale).
 *
 * `setLocale` reste le chemin normal : il persiste le choix et recharge, ce dont
 * les apps web et `passenger-app` ont besoin.
 */
export const setActiveLocale = (newLocale: SupportedLocales) => {
  _activeLocale = newLocale;
  if (_i18n) _i18n.locale = newLocale;
};
export const setLocale = (newLocale: SupportedLocales) => {
  storage.setItem('locale', newLocale);

  getConfig().reloadSystem();
};
